/**
 * Form Builder local state — schema being edited, selected field path, dirty flag.
 *
 * Field path là dot-notation: "" = root, "0" = root field index 0,
 * "1.fields.2" = sub-field index 2 của repeater tại root index 1.
 */
import { create } from "zustand";

import type {
  FieldType,
  FormFieldSchema,
  FormSchema,
} from "@/api/forms";

export type FieldPath = string; // "0", "1.fields.2", ...

interface BuilderState {
  // Data
  formCode: string | null;
  formName: string;
  formDescription: string;
  schema: FormSchema | null;
  initialSchema: FormSchema | null; // để diff dirty
  versionInfo: { version: number; status: string } | null;

  // UI
  selectedPath: FieldPath | null;
  isDirty: boolean;

  // Actions
  loadSchema: (
    schema: FormSchema,
    meta: { code: string; name: string; description: string; version: number; status: string },
  ) => void;
  resetToInitial: () => void;
  selectField: (path: FieldPath | null) => void;
  setMeta: (name: string, description: string) => void;
  addField: (parentPath: FieldPath | null, type: FieldType) => void;
  removeField: (path: FieldPath) => void;
  moveField: (path: FieldPath, direction: "up" | "down") => void;
  updateField: (path: FieldPath, patch: Partial<FormFieldSchema>) => void;
  duplicateField: (path: FieldPath) => void;
}

// ----------------------------------------------------------------------------
// Path helpers
// ----------------------------------------------------------------------------
export function parsePath(path: FieldPath): (string | number)[] {
  // "1.fields.2" → ["1", "fields", "2"] → [1, "fields", 2]
  if (!path) return [];
  return path.split(".").map((p) => (/^\d+$/.test(p) ? Number(p) : p));
}

/** Lấy node tại path. Return null nếu path invalid hoặc out-of-bounds. */
function getAtPath(
  fields: FormFieldSchema[],
  path: FieldPath,
): { parent: FormFieldSchema[]; index: number; node: FormFieldSchema } | null {
  const parts = parsePath(path);
  let cur: FormFieldSchema[] = fields;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (typeof p !== "number") continue;
    const node = cur[p];
    if (!node) return null;
    if (i === parts.length - 1) {
      return { parent: cur, index: p, node };
    }
    cur = node.fields ?? [];
  }
  return null;
}

const cloneArr = (xs: FormFieldSchema[]): FormFieldSchema[] =>
  xs.map((x) => ({ ...x }));

function setAtPath(
  fields: FormFieldSchema[],
  path: FieldPath,
  patch: Partial<FormFieldSchema>,
): FormFieldSchema[] {
  const parts = parsePath(path);
  const out = cloneArr(fields);
  let cur: FormFieldSchema[] = out;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (typeof p !== "number") continue;
    const node = cur[p];
    if (!node) return out;
    if (i === parts.length - 1) {
      cur[p] = { ...node, ...patch };
      return out;
    }
    const cloned = { ...node, fields: cloneArr(node.fields ?? []) };
    cur[p] = cloned;
    cur = cloned.fields ?? [];
  }
  return out;
}

function removeAtPath(
  fields: FormFieldSchema[],
  path: FieldPath,
): FormFieldSchema[] {
  const parts = parsePath(path);
  const out = cloneArr(fields);
  let cur: FormFieldSchema[] = out;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (typeof p !== "number") continue;
    const node = cur[p];
    if (!node) return out;
    if (i === parts.length - 1) {
      cur.splice(p, 1);
      return out;
    }
    const cloned = { ...node, fields: cloneArr(node.fields ?? []) };
    cur[p] = cloned;
    cur = cloned.fields ?? [];
  }
  return out;
}

function moveAtPath(
  fields: FormFieldSchema[],
  path: FieldPath,
  dir: "up" | "down",
): FormFieldSchema[] {
  const parts = parsePath(path);
  const out = cloneArr(fields);
  let cur: FormFieldSchema[] = out;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (typeof p !== "number") continue;
    const node = cur[p];
    if (!node) return fields;
    if (i === parts.length - 1) {
      const j = dir === "up" ? p - 1 : p + 1;
      if (j < 0 || j >= cur.length) return fields;
      const swap = cur[j];
      if (!swap) return fields;
      cur[p] = swap;
      cur[j] = node;
      return out;
    }
    const cloned = { ...node, fields: cloneArr(node.fields ?? []) };
    cur[p] = cloned;
    cur = cloned.fields ?? [];
  }
  return out;
}

function appendAtPath(
  fields: FormFieldSchema[],
  parentPath: FieldPath | null,
  newField: FormFieldSchema,
): FormFieldSchema[] {
  if (parentPath == null || parentPath === "") {
    return [...cloneArr(fields), newField];
  }
  const out = cloneArr(fields);
  const parts = parsePath(parentPath);
  let cur: FormFieldSchema[] = out;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (typeof p !== "number") continue;
    const node = cur[p];
    if (!node) return out;
    if (i === parts.length - 1) {
      cur[p] = {
        ...node,
        fields: [...cloneArr(node.fields ?? []), newField],
      };
      return out;
    }
    const cloned = { ...node, fields: cloneArr(node.fields ?? []) };
    cur[p] = cloned;
    cur = cloned.fields ?? [];
  }
  return out;
}

function genFieldName(type: FieldType, existing: string[]): string {
  const base = type;
  let i = 1;
  while (existing.includes(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

function defaultField(type: FieldType, existingNames: string[]): FormFieldSchema {
  const name = genFieldName(type, existingNames);
  const base: FormFieldSchema = {
    name,
    type,
    label: name.replace(/_/g, " "),
    required: false,
  };
  switch (type) {
    case "select":
    case "radio":
    case "multi_select":
      return { ...base, options: [{ value: "a", label: "Lựa chọn A" }] };
    case "async_select":
      return { ...base, binding: { erp_model: "res.partner" } };
    case "repeater":
      return { ...base, fields: [], min_items: 1 };
    case "number":
      return { ...base, min: 0 };
    case "computed":
      return { ...base, calc: { "+": [0, 0] } };
    default:
      return base;
  }
}

function collectAllNames(fields: FormFieldSchema[]): string[] {
  const out: string[] = [];
  const walk = (xs: FormFieldSchema[]): void => {
    for (const f of xs) {
      out.push(f.name);
      if (f.fields) walk(f.fields);
    }
  };
  walk(fields);
  return out;
}

// ----------------------------------------------------------------------------
// Store
// ----------------------------------------------------------------------------
function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const useFormBuilderStore = create<BuilderState>((set, get) => ({
  formCode: null,
  formName: "",
  formDescription: "",
  schema: null,
  initialSchema: null,
  versionInfo: null,
  selectedPath: null,
  isDirty: false,

  loadSchema: (schema, meta) => {
    set({
      schema: structuredClone(schema),
      initialSchema: structuredClone(schema),
      formCode: meta.code,
      formName: meta.name,
      formDescription: meta.description,
      versionInfo: { version: meta.version, status: meta.status },
      selectedPath: null,
      isDirty: false,
    });
  },

  resetToInitial: () => {
    const { initialSchema } = get();
    if (!initialSchema) return;
    set({
      schema: structuredClone(initialSchema),
      isDirty: false,
      selectedPath: null,
    });
  },

  selectField: (path) => set({ selectedPath: path }),

  setMeta: (name, description) =>
    set((s) => ({
      formName: name,
      formDescription: description,
      isDirty: s.isDirty || s.formName !== name || s.formDescription !== description,
    })),

  addField: (parentPath, type) => {
    const { schema } = get();
    if (!schema) return;
    const existingNames = collectAllNames(schema.fields);
    const newField = defaultField(type, existingNames);
    const fields = appendAtPath(schema.fields, parentPath, newField);
    set({
      schema: { ...schema, fields },
      isDirty: true,
    });
  },

  removeField: (path) => {
    const { schema } = get();
    if (!schema) return;
    const fields = removeAtPath(schema.fields, path);
    set({
      schema: { ...schema, fields },
      selectedPath: null,
      isDirty: true,
    });
  },

  moveField: (path, dir) => {
    const { schema } = get();
    if (!schema) return;
    const fields = moveAtPath(schema.fields, path, dir);
    if (fields === schema.fields) return; // no-op
    set({ schema: { ...schema, fields }, isDirty: true });
  },

  updateField: (path, patch) => {
    const { schema, initialSchema } = get();
    if (!schema) return;
    const fields = setAtPath(schema.fields, path, patch);
    const next = { ...schema, fields };
    set({
      schema: next,
      isDirty: !deepEqual(next, initialSchema),
    });
  },

  duplicateField: (path) => {
    const { schema } = get();
    if (!schema) return;
    const found = getAtPath(schema.fields, path);
    if (!found) return;
    const existing = collectAllNames(schema.fields);
    const copy = structuredClone(found.node);
    copy.name = genFieldName(copy.type, existing);
    copy.label = `${copy.label} (copy)`;
    // Append vào parent
    const parts = parsePath(path);
    const parentParts = parts.slice(0, -1);
    const parentPath = parentParts.length ? parentParts.join(".") : null;
    const fields = appendAtPath(schema.fields, parentPath, copy);
    set({ schema: { ...schema, fields }, isDirty: true });
  },
}));

// ----------------------------------------------------------------------------
// Selectors helpers
// ----------------------------------------------------------------------------
export function getFieldAt(
  schema: FormSchema | null,
  path: FieldPath | null,
): FormFieldSchema | null {
  if (!schema || path === null) return null;
  const found = getAtPath(schema.fields, path);
  return found ? found.node : null;
}

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  textarea: "Văn bản dài",
  number: "Số",
  date: "Ngày",
  datetime: "Ngày + giờ",
  select: "Dropdown",
  async_select: "Dropdown ERP",
  multi_select: "Multi-select",
  checkbox: "Checkbox",
  radio: "Radio",
  file: "File upload",
  section: "Section (nhóm)",
  repeater: "Repeater (mảng)",
  computed: "Computed (tính)",
};
